import {AxiosInstance} from "axios";
import {Poll, PollAnswer, PollEntry, PollEntryAction} from "@/program/interfaces/Program";
import Consumer from "@/core/interfaces/consumers/Consumer";

/**
 * The consumer resource path on the HDS server, substituted into the
 * {consumer_path} placeholder of poll entry action and cancel routes.
 */
const consumerPath = (consumer: Consumer): string => consumer.type === 'beneficiary'
    ? `customers/${consumer.customer_id}/beneficiaries/${consumer.id}`
    : `customers/${consumer.id}`;

/**
 * Normalize a poll entry to its list of actions: the actions payload when
 * present, otherwise the legacy flat fields (one route and host shared by
 * one or several action_body items).
 */
export const pollEntryActions = (entry: PollEntry): PollEntryAction[] => {
    if (entry.actions?.length) {
        return entry.actions;
    }

    if (!entry.action_route) {
        return [];
    }

    const bodies = Array.isArray(entry.action_body) ? entry.action_body : [entry.action_body];

    return bodies.map(body => ({
        host: entry.action_host,
        method: entry.action_method,
        route: entry.action_route!,
        body,
        cancel_method: entry.cancel_method,
        cancel_route: entry.cancel_route,
        cancel_body: entry.cancel_body,
    }));
};

/**
 * The declared information a poll entry records on the consumer, when it carries
 * such an action. Reading it from the entry avoids keying the behaviour on a
 * label: the seeder describes what the answer means.
 */
export const pollEntryDeclaredInformation = (entry: PollEntry): Record<string, unknown> | null => {
    for (const action of pollEntryActions(entry)) {
        const declared = (action.body as {data?: {declared_information?: Record<string, unknown>}} | null)
            ?.data?.declared_information;

        if (declared) {
            return declared;
        }
    }

    return null;
};

export default function (apiClient: AxiosInstance, hdsClient: AxiosInstance) {
    return {
        /**
         * Get a list of polls.
         * @param params
         */
        async index(params: object): Promise<Poll[]> {
            return apiClient.get('polls', {params})
                .then(response => response.data.data);
        },
        /**
         * Toggle the enabled state of a poll.
         *
         * @param pollId - The poll id.
         * @return {Promise<{enabled: boolean}>}
         */
        async toggle(pollId: number): Promise<{ enabled: boolean }> {
            return apiClient.patch(`polls/${pollId}/toggle`)
                .then(response => response.data.data);
        },

        /**
         * Submit a poll entry.
         * @param pollId
         * @param entryId
         * @param consumerKey
         */
        async submit(pollId: number, entryId: number, consumerKey: string): Promise<{
            data: PollAnswer,
            meta: { refreshed_programs: number[] }
        }> {
            return apiClient.post(`polls/${pollId}/submit`, {
                consumer_key: consumerKey,
                entry_id: entryId
            }).then(response => response.data);
        },

        /**
         * Cancel a poll entry.
         * @param pollId  The poll ID.
         * @param consumerKey  The consumer Key.
         */
        async cancel(pollId: number, consumerKey: string): Promise<{
            data: {message: string},
            meta: { refreshed_programs: number[] }
        }> {
            return apiClient.post(`polls/${pollId}/cancel`, {
                consumer_key: consumerKey
            });
        },

        /**
         * Perform the actions of a poll entry, each on its own host and route.
         * @param entry
         * @param customer
         */
        async actions(entry: PollEntry, customer: Consumer): Promise<object[]> {
            const promises = pollEntryActions(entry).map(action => {
                const client = action.host == 'hds' ? hdsClient : apiClient;
                const body = action.body ? {...action.body} : action.body;

                if (body && 'customer_id' in body) {
                    body['customer_id'] = customer.id;
                }

                if (body && 'consumer_key' in body) {
                    body['consumer_key'] = customer.key;
                }

                return client.request({
                    url: action.route.replace('{consumer_path}', consumerPath(customer)),
                    method: action.method ?? 'GET',
                    data: body
                }).then(response => response.data.data);
            });

            return Promise.all(promises);
        },

        /**
         * Perform the cancel action of every poll entry action carrying one.
         *
         * @param entry  The poll entry for which to perform the cancel actions.
         * @param responses  The responses from the action calls, used to replace {id} in the cancel routes if needed.
         * @param customer  The consumer for which to perform the cancel actions.
         */
        async cancelActions(entry: PollEntry, customer: Consumer, responses?: object[]): Promise<void[]> {
            const promises: Promise<void>[] = [];

            pollEntryActions(entry).forEach((action, index) => {
                if (!action.cancel_route) {
                    return;
                }

                const client = action.host == 'hds' ? hdsClient : apiClient;
                let url = action.cancel_route.replace('{consumer_path}', consumerPath(customer));
                const data = action.cancel_body ? {...action.cancel_body} : {};
                const response = responses?.[index];

                if (response && 'id' in response) {
                    url = url.replace('{id}', String((response as { id: number }).id));
                }

                if ('customer_id' in data) {
                    data['customer_id'] = customer.id;
                }

                if ('consumer_key' in data) {
                    data['consumer_key'] = customer.key;
                }

                promises.push(client.request({url, method: action.cancel_method ?? 'GET', data}));
            });

            return Promise.all(promises);
        },
    };
}
